import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CustomerService } from "services/CustomerService";
import { GetEmailStatusHistory } from "services/Dtos/GetEmailsDto";

interface EmailHistorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  emailId: number;
  subject: string;
  apiKey: string;
}

const EmailHistorySidebar: React.FC<EmailHistorySidebarProps> = ({
  isOpen,
  onClose,
  emailId,
  subject,
  apiKey,
}) => {
  const [history, setHistory] = useState<GetEmailStatusHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadHistory = async () => {
      if (!isOpen) return;

      try {
        setIsLoading(true);
        const response = await CustomerService.email.getEmailHistoryByEmailId(
          emailId,
          apiKey,
        );
        setHistory(response.data || []);
      } catch (err) {
        setError("Failed to load email history");
        toast.error("Failed to load email history", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();
  }, [isOpen, emailId]);

  const getStatusIcon = (eventType: string): string => {
    switch (eventType.toLowerCase()) {
      case "open":
        return "bi-envelope-open";
      case "click":
        return "bi-link-45deg";
      case "bounce":
        return "bi-exclamation-triangle";
      case "spamreport":
        return "bi-shield-exclamation";
      case "unsubscribe":
        return "bi-person-x";
      case "delivered":
        return "bi-check-circle";
      case "sent":
        return "bi-send";
      default:
        return "bi-info-circle";
    }
  };

  const getStatusColor = (eventType: string): string => {
    switch (eventType.toLowerCase()) {
      case "open":
      case "click":
      case "delivered":
      case "sent":
        return "text-success";
      case "bounce":
      case "spamreport":
        return "text-danger";
      case "unsubscribe":
        return "text-warning";
      default:
        return "text-info";
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`position-fixed top-0 end-0 bottom-0 start-0 bg-dark bg-opacity-50 ${
          !isOpen ? "d-none" : ""
        }`}
        onClick={onClose}
        style={{ zIndex: 1040 }}
      />

      {/* Sidebar */}
      <div
        className={`position-fixed top-0 end-0 bottom-0 bg-white shadow overflow-auto ${
          !isOpen ? "translate-end d-none" : ""
        }`}
        style={{ width: "400px", zIndex: 1045 }}
      >
        {/* Header */}
        <div className="p-3 border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Email History: {subject}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-3">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="d-flex justify-content-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : history.length > 0 ? (
            <div className="timeline">
              {history.map((event, index) => (
                <div key={index} className="d-flex mb-3">
                  <div className="d-flex flex-column align-items-center me-3">
                    <div className="mb-2">
                      <i
                        className={`bi ${getStatusIcon(event.eventType)} ${getStatusColor(event.eventType)}`}
                      ></i>
                    </div>
                    {index !== history.length - 1 && (
                      <div
                        className="border-start border-secondary"
                        style={{ height: "100%", width: "2px" }}
                      ></div>
                    )}
                  </div>
                  <div className="card flex-grow-1">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">{event.eventType}</h6>
                        <small className="text-muted">
                          {new Date(
                            event.unixTimestamp * 1000,
                          ).toLocaleString()}
                        </small>
                      </div>
                      {event.statusDetails && (
                        <p className="mb-0 small mt-2">{event.statusDetails}</p>
                      )}
                      {event.reason && (
                        <p className="mb-0 small text-danger mt-2">
                          {event.reason}
                        </p>
                      )}
                      {event.url && (
                        <a
                          href={event.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="small mt-2 d-inline-block"
                        >
                          View Details
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="alert alert-info" role="alert">
              No history available for this email.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="position-sticky bottom-0 p-3 bg-white border-top">
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <style>
        {`
          .timeline {
            position: relative;
            padding: 20px 0;
          }
        `}
      </style>
    </>
  );
};

export default EmailHistorySidebar;
